<script setup>
/* eslint-disable vue/no-bare-strings-in-template */
import { computed } from 'vue';
import format from 'date-fns/format';
import ReportLineChart from '../ReportLineChart.vue';
import ReportCard from '../ReportCard.vue';

const props = defineProps({
  analytics: {
    type: Object,
    default: () => ({}),
  },
  rankings: {
    type: Array,
    default: () => [],
  },
  dailySummary: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const pauseAnalytics = computed(() => props.analytics.pause_analytics || {});
const availability = computed(() => props.analytics.availability_changes || {});
const authentication = computed(
  () => props.analytics.authentication_activity || {}
);
const workHours = computed(() => props.analytics.work_hours || {});
const maxRanking = computed(() =>
  Math.max(...props.rankings.map(row => row.total_pause_sessions || 0), 1)
);

const formatDuration = minutes => {
  if (!minutes) return '0m';
  if (minutes < 60) return `${Math.round(minutes)}m`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${Math.round(minutes % 60)}m`;
};

const summaryCards = computed(() => [
  {
    label: 'Sessões de pausa',
    value: pauseAnalytics.value.total_pause_sessions || 0,
    icon: 'status',
  },
  {
    label: 'Tempo total em pausa',
    value: formatDuration(pauseAnalytics.value.total_pause_duration_minutes),
    icon: 'calendar-clock',
  },
  {
    label: 'Média por pausa',
    value: formatDuration(pauseAnalytics.value.average_pause_duration_minutes),
    icon: 'status',
  },
  {
    label: 'Mudanças de disponibilidade',
    value: availability.value.total_changes || 0,
    icon: 'arrow-swap',
  },
  {
    label: 'Logins',
    value: authentication.value.total_logins || 0,
    icon: 'status',
  },
  {
    label: 'Logouts',
    value: authentication.value.total_logouts || 0,
    icon: 'arrow-swap',
  },
  {
    label: 'Sessões encerradas por perda de sinal',
    value: workHours.value.timed_out_sessions || 0,
    icon: 'status',
  },
  {
    label: 'Horas conectadas (brutas)',
    value: formatDuration((workHours.value.total_duration_hours || 0) * 60),
    icon: 'calendar-clock',
  },
]);

const statusCards = computed(() => {
  const counts = props.analytics.current_statuses || {};
  return [
    { status: 'online', label: 'Online', value: counts.online || 0 },
    { status: 'busy', label: 'Ocupado', value: counts.busy || 0 },
    { status: 'pause', label: 'Pausa', value: counts.pause || 0 },
    { status: 'offline', label: 'Offline', value: counts.offline || 0 },
  ];
});

const recentEvents = computed(() => props.analytics.recent_events || []);

const eventDescription = event => {
  if (event.event_type === 'login') return 'entrou';
  if (event.event_type === 'logout') return 'saiu';
  if (event.event_type === 'pause') return 'iniciou uma pausa';
  if (event.event_type === 'availability_change') {
    return `alterou de ${event.from_status || '—'} para ${event.to_status || '—'}`;
  }
  if (event.event_type === 'message_sent') return 'enviou uma mensagem';
  if (event.event_type === 'conversation_updated')
    return 'atualizou uma conversa';
  if (event.event_type === 'contact_updated') return 'atualizou um contato';
  if (event.event_type === 'work_session') {
    if (!event.ended_at) return 'iniciou a jornada (em andamento)';
    return event.end_reason === 'heartbeat_timeout'
      ? 'jornada encerrada por perda de heartbeat'
      : 'encerrou a jornada';
  }
  return event.event_type;
};

const formatDateTime = value =>
  value ? format(new Date(value), 'dd/MM/yyyy HH:mm:ss') : '—';

const dataset = (label, data, color) => ({
  label,
  data,
  borderColor: color,
  backgroundColor: color,
});

const chartCollection = computed(() => ({
  labels: props.dailySummary.map(row => format(new Date(row.date), 'dd MMM')),
  datasets: [
    dataset(
      'Sessões de pausa',
      props.dailySummary.map(row => row.total_pause_sessions || 0),
      '#1f93ff'
    ),
    dataset(
      'Mudanças de disponibilidade',
      props.dailySummary.map(row => row.total_availability_changes || 0),
      '#14b8a6'
    ),
    dataset(
      'Logins',
      props.dailySummary.map(row => row.total_logins || 0),
      '#22c55e'
    ),
    dataset(
      'Logouts',
      props.dailySummary.map(row => row.total_logouts || 0),
      '#f97316'
    ),
  ],
}));
</script>

<template>
  <div class="space-y-4">
    <div class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
      <ReportCard
        v-for="card in summaryCards"
        :key="card.label"
        :is-loading="isLoading"
      >
        <div class="flex items-center gap-4">
          <span class="activity-icon">
            <fluent-icon :icon="card.icon" size="22" />
          </span>
          <div>
            <p class="mb-1 text-sm text-slate-500 dark:text-slate-400">
              {{ card.label }}
            </p>
            <strong class="text-3xl text-slate-900 dark:text-slate-50">{{
              card.value
            }}</strong>
          </div>
        </div>
      </ReportCard>
    </div>

    <ReportCard :is-loading="isLoading">
      <div>
        <h3 class="mb-4 text-lg font-medium text-slate-900 dark:text-slate-50">
          Status atual dos agentes
        </h3>
        <div class="grid grid-cols-2 gap-4 md:grid-cols-4">
          <div
            v-for="item in statusCards"
            :key="item.status"
            class="flex items-center gap-3 rounded-md border border-slate-100 p-3 dark:border-slate-700"
          >
            <span class="status-dot" :class="`status-dot--${item.status}`" />
            <div>
              <p class="mb-0 text-sm text-slate-500 dark:text-slate-400">
                {{ item.label }}
              </p>
              <strong class="text-xl text-slate-900 dark:text-slate-50">
                {{ item.value }}
              </strong>
            </div>
          </div>
        </div>
      </div>
    </ReportCard>

    <div class="grid grid-cols-1 gap-4 xl:grid-cols-12">
      <ReportCard class="xl:col-span-7" :is-loading="isLoading">
        <div class="mb-4">
          <h3
            class="mb-0 text-lg font-medium text-slate-900 dark:text-slate-50"
          >
            Evolução diária
          </h3>
        </div>
        <div v-if="props.dailySummary.length" class="w-full h-64">
          <ReportLineChart :collection="chartCollection" />
        </div>
        <div
          v-else
          class="flex items-center justify-center h-64 text-sm text-slate-500 dark:text-slate-400"
        >
          Sem atividade no período.
        </div>
      </ReportCard>

      <ReportCard class="xl:col-span-5" :is-loading="isLoading">
        <h3 class="mb-4 text-lg font-medium text-slate-900 dark:text-slate-50">
          Ranking de pausas
        </h3>
        <div class="space-y-4">
          <div
            v-for="row in rankings.slice(0, 8)"
            :key="row.user_id"
            class="grid grid-cols-[1fr_1fr_2rem] items-center gap-3 text-sm"
          >
            <span class="truncate text-slate-700 dark:text-slate-200">
              {{ row.rank }}. {{ row.user_name }}
            </span>
            <span
              class="h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700"
            >
              <span
                class="block h-full rounded-full bg-woot-500"
                :style="{
                  width: `${((row.total_pause_sessions || 0) / maxRanking) * 100}%`,
                }"
              />
            </span>
            <strong class="text-right text-slate-800 dark:text-slate-100">{{
              row.total_pause_sessions
            }}</strong>
          </div>
          <p
            v-if="!rankings.length"
            class="mb-0 text-sm text-slate-500 dark:text-slate-400"
          >
            Sem ranking no período.
          </p>
        </div>
        <p
          v-if="workHours.timed_out_sessions"
          class="mt-4 rounded-md bg-yellow-50 p-3 text-sm text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200"
        >
          Sessões encerradas por perda de heartbeat são não confirmadas e não
          representam necessariamente logout.
        </p>
      </ReportCard>
    </div>

    <ReportCard :is-loading="isLoading">
      <h3 class="mb-4 text-lg font-medium text-slate-900 dark:text-slate-50">
        Atividade recente
      </h3>
      <div
        class="max-h-80 divide-y divide-slate-100 overflow-auto dark:divide-slate-700"
      >
        <div
          v-for="event in recentEvents"
          :key="event.id"
          class="flex flex-wrap items-center justify-between gap-2 py-3 text-sm"
        >
          <div class="text-slate-700 dark:text-slate-200">
            <strong>{{ event.user_name }}</strong>
            {{ eventDescription(event) }}
          </div>
          <span class="text-slate-500 dark:text-slate-400">
            {{ formatDateTime(event.started_at) }}
            <span v-if="event.ip_address" class="ml-2">
              IP {{ event.ip_address }}
            </span>
          </span>
        </div>
        <p
          v-if="!recentEvents.length"
          class="mb-0 py-6 text-center text-sm text-slate-500 dark:text-slate-400"
        >
          Sem atividade no período.
        </p>
      </div>
    </ReportCard>
  </div>
</template>

<style lang="scss" scoped>
.activity-icon {
  @apply flex items-center justify-center flex-shrink-0 w-11 h-11 rounded-full text-woot-500 bg-woot-50 dark:bg-woot-900/40;
}

.status-dot {
  @apply w-3 h-3 rounded-full;

  &--online {
    @apply bg-green-500;
  }

  &--busy {
    @apply bg-yellow-500;
  }

  &--pause {
    @apply bg-woot-500;
  }

  &--offline {
    @apply bg-slate-400;
  }
}
</style>
